import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Checkbox,
    FormControlLabel,
    Slide,
    IconButton,
    TextField,
    InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import SearchIcon from "@mui/icons-material/Search";
import { getMachineLocations } from "../../Services/authService";
import { useThemeContext } from "../../Context/ThemeContext";
import { DARKCOLORS, LIGHTCOLORS } from "../../Utils/constants";

// Slide Transition Function
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="left" ref={ref} {...props} timeout={800} />;
});

const LocationPopup = ({ open, onClose, selectedMachineLocations, onSubmit, machineType, excludeMachineType }) => {
    const { t } = useTranslation();

    const { mode } = useThemeContext();
    const [locations, setLocations] = useState([]);
    const [filteredLocations, setFilteredLocations] = useState([]);
    const [selectedMachines, setSelectedMachines] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");

    const modalBackgroundColor = mode === "dark" ? DARKCOLORS.BackgroundCard : LIGHTCOLORS.BackgroundCard;
    const textColor = mode === "dark" ? DARKCOLORS.BodyLightBackground : LIGHTCOLORS.BodyLightBackground;
    const searchBackground = mode === 'dark' ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
    const dividerColor = mode === 'dark' ? DARKCOLORS.Primary100 : LIGHTCOLORS.Primary100;
    const btnColor = mode === 'dark' ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;
  const scroll1 = mode === 'light' ? LIGHTCOLORS.BodyDarkBackground : DARKCOLORS.BodyLightBackground;

  const scroll = mode === 'light' ? LIGHTCOLORS.GreyScale40 : DARKCOLORS.GreyScale40;

    useEffect(() => {
        const fetchLocations = async () => {
            try {
                const data = await getMachineLocations(); // Fetch locations from backend
                // If a machineType filter is specified, keep only matching machines
                const filtered = machineType
                    ? data
                          .map((loc) => ({
                              ...loc,
                              machines: loc.machines.filter(
                                  (m) => m.machineType === machineType
                              ),
                          }))
                          .filter((loc) => loc.machines.length > 0)
                    : data;

                // If an excludeMachineType filter is specified, remove those machines
                const withExclusion = excludeMachineType
                    ? filtered
                          .map((loc) => ({
                              ...loc,
                              machines: loc.machines.filter(
                                  (m) => m.machineType !== excludeMachineType
                              ),
                          }))
                          .filter((loc) => loc.machines.length > 0)
                    : filtered;

                setLocations(withExclusion);
                setFilteredLocations(withExclusion);
                setSelectedMachines(selectedMachineLocations || []);
            } catch (error) {
                console.error("Error fetching locations:", error);
            }
        };
        fetchLocations();
    }, [selectedMachineLocations, machineType, excludeMachineType]);

    const handleToggleMachine = (machineId) => {
        setSelectedMachines((prev) =>
            prev.includes(machineId)
                ? prev.filter((id) => id !== machineId)
                : [...prev, machineId]
        );
    };

    const handleSelectAll = (checked) => {
        if (checked) {
            const allMachineIds = filteredLocations.flatMap((loc) =>
                loc.machines.map((machine) => machine.machineId)
            );
            setSelectedMachines(allMachineIds);
        } else {
            setSelectedMachines([]);
        }
    };

    const handleSearch = (event) => {
        const query = event.target.value.toLowerCase();
        setSearchQuery(query);
        const filtered = locations.filter(
            (loc) =>
                (loc.city.toLowerCase().includes(query) ||
                loc.machines.some((machine) =>
                    `${machine.machineNo} ${machine.street}`.toLowerCase().includes(query)
                )) && loc.machines.length > 0
        );
        setFilteredLocations(filtered);
    };

    // Guard the empty list: .every() on [] is true, which would tick
    // "Select All" and report all-selected when nothing is visible
    const isAllSelected =
        filteredLocations.length > 0 &&
        filteredLocations.every((loc) =>
            loc.machines.every((machine) => selectedMachines.includes(machine.machineId))
        );

    const handleSubmit = () => {
        const selectedMachineDetails = locations
            .map((location) => ({
                location: location.city,
                machines: location.machines.filter((machine) =>
                    selectedMachines.includes(machine.machineId)
                ),
            }))
            .filter((loc) => loc.machines.length > 0); // Include only locations with selected machines

        onSubmit(selectedMachineDetails, isAllSelected); // Pass full details to the parent
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            TransitionComponent={Transition}
            PaperProps={{
                sx: {
                    position: "absolute",
                    right: 50,
                    margin: 0,
                    width: "350px",
                    "@media (max-width: 427px)": {
                        right: 5,
                        width: "300px",
                    },
                    maxHeight: "70vh",
                    borderRadius: 3,
                    background: modalBackgroundColor,
                },
            }}
        >
            {/* Title with Close Icon */}
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 2,
                    px: 2,
                }}
            >
                <button type="button" style={{ background: btnColor, padding: "10px 15px", fontSize: "13px" }} className='save-btn' onClick={handleSubmit}>{t('membershipCards.save')}</button>
                <IconButton onClick={onClose} aria-label="close">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 3 ,
                 marginRight: 1,
              overflowY: "auto",
            '&::-webkit-scrollbar': {
              width: '8px !important',
              height: '8px !important',
              padding: '2px !important',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: scroll1,
              borderRadius: '10px',
              border: `1px solid ${scroll}`,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: scroll,
              borderRadius: '10px',
              width: '3px !important',
              border: '3px solid transparent',
              backgroundClip: 'padding-box',
              '&:hover': {
                backgroundColor: scroll,
              },
            },
             }}>
                {/* Search Bar */}
                <TextField
                    placeholder={t("membershipCards.search")}
                    value={searchQuery}
                    onChange={handleSearch}
                    style={{ borderRadius: "24px", background: searchBackground, width: "100%", marginBottom: 10 }}
                    size="small"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                                border: "none", // Removes the border from the outline
                            },
                        },
                    }}
                />

                {/* Select All */}
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={isAllSelected}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                        />
                    }
                    label={t("sales_analytics.SelectAll")}
                    sx={{ color: textColor, mb: 1 }}
                />

                {/* Locations and Machines */}
                {filteredLocations.length === 0 && (
                    <p style={{ color: textColor, padding: "20px 0 5px", margin: 0, textAlign: "center", borderTop: `1px solid ${dividerColor}` }}>
                        {t("vendingMachines.noMatchingLocations")}
                    </p>
                )}
                {filteredLocations.map((location, index) => (
                    <Box key={index}>
                        {/* Location Name */}
                        <p className="popup-text" style={{ padding: "15px 0px 5px", margin: 0, borderTop: `1px solid ${dividerColor}` }}>{location.city}</p>

                        {/* Machines */}
                        {location.machines.map((machine) => (
                            <FormControlLabel
                                key={machine.machineId}
                                control={
                                    <Checkbox
                                        checked={selectedMachines.includes(machine.machineId)}
                                        onChange={() => handleToggleMachine(machine.machineId)}
                                    />
                                }
                                label={
                                    <span title={`${machine.location} – ${machine.machineNo}`}>
                                        {machine.location} – {machine.machineNo}
                                    </span>
                                }
                                className="add-machine"
                                sx={{ color: textColor, display: "block" }}
                            />
                        ))}
                    </Box>
                ))}
            </DialogContent>
        </Dialog>
    );
};

export default LocationPopup;
